import { useDeferredValue, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Download, ExternalLink, Loader2 } from "lucide-react";

import {
  MegaventoryPurchaseInvoiceExternalLink,
  PurchaseOrderHubLinkWithMegaventory,
} from "@/components/mrp/MegaventoryPurchaseOrderLinks";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { HoverHintTrigger, PageTitleWithHelp } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";
import { mrpLocationChipStyles } from "@/lib/mrp";
import { CashFlowBarChart } from "./CashFlowBarChart";
import {
  addUtcMonthsIso,
  CASH_FLOW_SOURCE_KIND_LABEL,
  fetchCashFlowForecast,
  firstOfUtcMonth,
  type CashFlowDirection,
  type CashFlowForecastBucket,
  type CashFlowForecastLine,
  type CashFlowGranularity,
  type CashFlowSourceKind,
  type CashFlowUnknownExpense,
} from "./cashFlowApi";
import { apAgingImportHoverHint, arAgingImportHoverHint } from "./cashFlowAgingImportHints";
import { downloadCashFlowScheduledLinesCsv } from "./cashFlowExport";
import { QboAgingImportModal } from "./QboAgingImportModal";

type CashFlowPageTab = "overview" | "scheduled" | "unknown";

const HELP_TEXT = [
  "Expected cash timing from open purchase orders (Incoming parts arrival dates and payment terms), ",
  "GIPI/TSH purchase invoices still open in QuickBooks, CBP periodic monthly statement debits (the 15th business day after the entry month), open sales orders (expected ship-by and terms), and GOSI sales invoices still open in QuickBooks.",
  "",
  "After you import aging CSVs with Import AP aging and Import AR aging, GIPI/TSH outflows and GOSI inflows here are limited to documents on those reports with an open balance. ",
  "Paid documents absent from the aging files are omitted. Amounts use the QuickBooks open balance when available.",
  "",
  "Open PO lines exclude quantities already on non-cancelled purchase invoices. Open sales orders show only the ",
  "uninvoiced remainder after linked GOSI totals. All amounts are shown in USD; CNY is converted using daily CNY-per-USD rates ",
  "(Megaventory monthly averages by day — see Unknown expenses tab for lines missing dates needed for conversion).",
].join(" ");

const HORIZON_MONTH_OPTIONS = [3, 6, 12] as const;

const DISPLAY_CURRENCY = "USD";

function formatMoney(amount: number, _currency?: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: DISPLAY_CURRENCY,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${DISPLAY_CURRENCY}`;
  }
}

function formatDate(iso: string | null): string {
  if (iso === null || iso.trim() === "") return "—";
  const parsed = new Date(`${iso.includes("T") ? iso.slice(0, 10) : iso}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString();
}

function formatPeriodLabel(bucket: CashFlowForecastBucket, granularity: CashFlowGranularity): string {
  if (granularity === "month") {
    const parsed = new Date(`${bucket.period_start}T12:00:00`);
    return parsed.toLocaleDateString(undefined, { month: "short", year: "numeric" });
  }
  return `${formatDate(bucket.period_start)} – ${formatDate(bucket.period_end)}`;
}

interface FlatBucketRow {
  periodLabel: string;
  currency: string;
  inflow: number;
  outflow: number;
  net: number;
}

function flattenBuckets(
  buckets: CashFlowForecastBucket[],
  granularity: CashFlowGranularity,
): FlatBucketRow[] {
  const byPeriod = new Map<string, FlatBucketRow>();
  for (const bucket of buckets) {
    const periodLabel = formatPeriodLabel(bucket, granularity);
    let row = byPeriod.get(periodLabel);
    if (!row) {
      row = { periodLabel, currency: "USD", inflow: 0, outflow: 0, net: 0 };
      byPeriod.set(periodLabel, row);
    }
    for (const totals of Object.values(bucket.by_currency)) {
      row.inflow += totals.inflow;
      row.outflow += totals.outflow;
      row.net += totals.net;
    }
  }
  return [...byPeriod.values()];
}

function TimelineSummaryTable({ rows }: { rows: FlatBucketRow[] }) {
  if (rows.length === 0) {
    return <p className="text-sm text-slate-500">No scheduled cash movements in this horizon.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-slate-600">
            <th className="py-2 pr-3 font-medium">Period</th>
            <th className="py-2 pr-3 font-medium text-right">Inflow (USD)</th>
            <th className="py-2 pr-3 font-medium text-right">Outflow (USD)</th>
            <th className="py-2 font-medium text-right">Net (USD)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={`${row.periodLabel}-${index}`} className="border-b border-slate-100">
              <td className="py-2 pr-3">{row.periodLabel}</td>
              <td className="py-2 pr-3 text-right tabular-nums text-emerald-700">
                {row.inflow > 0 ? formatMoney(row.inflow) : "—"}
              </td>
              <td className="py-2 pr-3 text-right tabular-nums text-rose-700">
                {row.outflow > 0 ? formatMoney(row.outflow) : "—"}
              </td>
              <td
                className={cn(
                  "py-2 text-right tabular-nums font-medium",
                  row.net >= 0 ? "text-emerald-800" : "text-rose-800",
                )}
              >
                {formatMoney(row.net)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DirectionBadge({ direction }: { direction: CashFlowDirection }) {
  return (
    <span
      className={cn(
        "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
        direction === "inflow" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800",
      )}
    >
      {direction === "inflow" ? "Inflow" : "Outflow"}
    </span>
  );
}

function LineTable({
  rows,
  emptyMessage,
}: {
  rows: CashFlowForecastLine[];
  emptyMessage: string;
}) {
  if (rows.length === 0) {
    return <p className="text-sm text-slate-500">{emptyMessage}</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-slate-600">
            <th className="py-2 pr-3 font-medium">Due</th>
            <th className="py-2 pr-3 font-medium">Direction</th>
            <th className="py-2 pr-3 font-medium">Source</th>
            <th className="py-2 pr-3 font-medium">Document</th>
            <th className="py-2 pr-3 font-medium">Counterparty</th>
            <th className="py-2 pr-3 font-medium text-right">Amount</th>
            <th className="py-2 pr-3 font-medium">Terms</th>
            <th className="py-2 font-medium">Anchor</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={`${row.source_kind}-${row.document_id}-${row.due_date}-${index}`} className="border-b border-slate-100">
              <td className="py-2 pr-3 whitespace-nowrap">{formatDate(row.due_date)}</td>
              <td className="py-2 pr-3">
                <DirectionBadge direction={row.direction} />
              </td>
              <td className="py-2 pr-3 whitespace-nowrap">{CASH_FLOW_SOURCE_KIND_LABEL[row.source_kind]}</td>
              <td className="py-2 pr-3">
                <div className="flex flex-col gap-0.5">
                  {row.link_path ? (
                    <Link to={row.link_path} className="text-blue-700 hover:underline inline-flex items-center gap-1">
                      {row.document_ref}
                      <ExternalLink className="h-3 w-3" aria-hidden />
                    </Link>
                  ) : (
                    <span>{row.document_ref}</span>
                  )}
                  {row.detail ? <span className="text-xs text-slate-500">{row.detail}</span> : null}
                  {row.location_code ? (
                    <span className="text-xs text-slate-500">Location: {row.location_code}</span>
                  ) : null}
                </div>
              </td>
              <td className="py-2 pr-3">{row.counterparty ?? "—"}</td>
              <td className="py-2 pr-3 text-right tabular-nums">{formatMoney(row.amount)}</td>
              <td className="py-2 pr-3">{row.payment_terms_label ?? "—"}</td>
              <td className="py-2 whitespace-nowrap">{formatDate(row.anchor_date)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

type UnknownExpenseSortKey = "document" | "supplier" | "amount" | "reasons";

const UNKNOWN_EXPENSE_SORT_LABEL: Record<UnknownExpenseSortKey, string> = {
  document: "Document",
  supplier: "Supplier",
  amount: "Amount",
  reasons: "Reasons",
};

function compareUnknownExpenseRows(
  left: CashFlowUnknownExpense,
  right: CashFlowUnknownExpense,
  key: UnknownExpenseSortKey,
  direction: "asc" | "desc",
): number {
  const factor = direction === "asc" ? 1 : -1;
  if (key === "amount") {
    return (left.amount - right.amount) * factor;
  }
  if (key === "document") {
    return left.document_ref.localeCompare(right.document_ref, undefined, {
      numeric: true,
      sensitivity: "base",
    }) * factor;
  }
  if (key === "supplier") {
    return (left.counterparty ?? "").localeCompare(right.counterparty ?? "", undefined, {
      numeric: true,
      sensitivity: "base",
    }) * factor;
  }
  const leftReasons = left.reasons.join("; ");
  const rightReasons = right.reasons.join("; ");
  return leftReasons.localeCompare(rightReasons, undefined, { sensitivity: "base" }) * factor;
}

function UnknownExpenseSortTh({
  column,
  children,
  align = "left",
  className,
  sortKey,
  sortDir,
  onSort,
}: {
  column: UnknownExpenseSortKey;
  children: ReactNode;
  align?: "left" | "right";
  className?: string;
  sortKey: UnknownExpenseSortKey;
  sortDir: "asc" | "desc";
  onSort: (key: UnknownExpenseSortKey) => void;
}): JSX.Element {
  const active = sortKey === column;
  return (
    <th
      scope="col"
      className={cn("py-2 pr-3 font-medium text-slate-600", align === "right" && "text-right", className)}
      aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className={cn(
          "group inline-flex w-full min-w-0 items-center gap-0.5 font-inherit text-inherit hover:text-slate-800",
          align === "right" && "justify-end",
        )}
        aria-label={`Sort by ${UNKNOWN_EXPENSE_SORT_LABEL[column]}`}
      >
        {children}
        <span
          className={cn("shrink-0 text-[10px] leading-none text-slate-500", !active && "opacity-0 group-hover:opacity-70")}
          aria-hidden
        >
          {active ? (sortDir === "asc" ? "▲" : "▼") : "▲"}
        </span>
      </button>
    </th>
  );
}

function UnknownExpensesPanel({ rows }: { rows: CashFlowUnknownExpense[] }) {
  const [sortState, setSortState] = useState<{ column: UnknownExpenseSortKey; dir: "asc" | "desc" }>({
    column: "amount",
    dir: "desc",
  });

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((left, right) => compareUnknownExpenseRows(left, right, sortState.column, sortState.dir));
    return copy;
  }, [rows, sortState.column, sortState.dir]);

  const onSortHeader = (column: UnknownExpenseSortKey): void => {
    setSortState((previous) => {
      if (previous.column === column) {
        return { column, dir: previous.dir === "asc" ? "desc" : "asc" };
      }
      return { column, dir: column === "amount" ? "desc" : "asc" };
    });
  };

  if (rows.length === 0) {
    return (
      <p className="text-sm text-slate-500">
        No outflows are missing dates, terms, or amounts. Everything in scope can be placed on the forecast
        timeline.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">
        These outflows cannot be placed on the timeline until arrival dates, terms, or amounts are fixed.
        Update dates on{" "}
        <Link to="/mrp/incoming" className="text-blue-700 hover:underline">
          Incoming parts
        </Link>{" "}
        or open the purchase order in MRP.
      </p>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left">
              <UnknownExpenseSortTh column="document" sortKey={sortState.column} sortDir={sortState.dir} onSort={onSortHeader}>
                Document
              </UnknownExpenseSortTh>
              <UnknownExpenseSortTh column="supplier" sortKey={sortState.column} sortDir={sortState.dir} onSort={onSortHeader}>
                Supplier
              </UnknownExpenseSortTh>
              <UnknownExpenseSortTh
                column="amount"
                align="right"
                sortKey={sortState.column}
                sortDir={sortState.dir}
                onSort={onSortHeader}
              >
                Amount
              </UnknownExpenseSortTh>
              <UnknownExpenseSortTh
                column="reasons"
                className="pr-0"
                sortKey={sortState.column}
                sortDir={sortState.dir}
                onSort={onSortHeader}
              >
                Reasons
              </UnknownExpenseSortTh>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, index) => (
              <tr key={`unknown-${row.document_id}-${row.document_ref}-${index}`} className="border-b border-slate-100">
                <td className="py-2 pr-3">
                  {row.source_kind === "open_po" &&
                  row.link_path &&
                  row.document_id != null &&
                  Number.isFinite(Number.parseInt(row.document_id, 10)) ? (
                    <PurchaseOrderHubLinkWithMegaventory
                      purchaseOrderId={Number.parseInt(row.document_id, 10)}
                      poNumber={row.document_ref}
                      purchaseOrderMvId={
                        row.purchase_order_mv_id != null && Number.isFinite(row.purchase_order_mv_id)
                          ? row.purchase_order_mv_id
                          : null
                      }
                    />
                  ) : row.source_kind === "gipi" &&
                    row.purchase_invoice_mv_id != null &&
                    Number.isFinite(row.purchase_invoice_mv_id) ? (
                    <span className="inline-flex min-w-0 items-center gap-1">
                      {row.link_path ? (
                        <Link to={row.link_path} className="text-blue-700 hover:underline">
                          {row.document_ref}
                        </Link>
                      ) : (
                        <span>{row.document_ref}</span>
                      )}
                      <MegaventoryPurchaseInvoiceExternalLink
                        purchaseInvoiceMvId={row.purchase_invoice_mv_id}
                        mvNumber={row.document_ref}
                      />
                    </span>
                  ) : row.link_path ? (
                    <Link to={row.link_path} className="text-blue-700 hover:underline">
                      {row.document_ref}
                    </Link>
                  ) : (
                    row.document_ref
                  )}
                  {row.detail ? <div className="text-xs text-slate-500">{row.detail}</div> : null}
                  {row.location_code ? (
                    <div className="text-xs text-slate-500">Location: {row.location_code}</div>
                  ) : null}
                </td>
                <td className="py-2 pr-3">{row.counterparty ?? "—"}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{formatMoney(row.amount)}</td>
                <td className="py-2">
                  <ul className="flex flex-wrap gap-1">
                    {row.reasons.map((reason) => (
                      <li
                        key={reason}
                        className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-900"
                      >
                        {reason}
                      </li>
                    ))}
                  </ul>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function CashFlowForecastPage() {
  const monthStart = useMemo(() => firstOfUtcMonth(new Date()), []);
  const [horizonMonths, setHorizonMonths] = useState<(typeof HORIZON_MONTH_OPTIONS)[number]>(6);
  const [granularity, setGranularity] = useState<CashFlowGranularity>("week");
  const [directionFilter, setDirectionFilter] = useState<"all" | CashFlowDirection>("all");
  const [sourceFilter, setSourceFilter] = useState<"all" | CashFlowSourceKind>("all");
  const [locationFilter, setLocationFilter] = useState<string>("all");
  const [searchDraft, setSearchDraft] = useState("");
  const search = useDeferredValue(searchDraft.trim().toLowerCase());
  const [activeTab, setActiveTab] = useState<CashFlowPageTab>("overview");
  const [apAgingModalOpen, setApAgingModalOpen] = useState(false);
  const [arAgingModalOpen, setArAgingModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const from = monthStart;
  const to = useMemo(() => addUtcMonthsIso(monthStart, horizonMonths), [monthStart, horizonMonths]);

  const forecastQuery = useQuery({
    queryKey: ["accounting", "cash-flow-forecast", from, to, granularity],
    queryFn: () => fetchCashFlowForecast({ from, to, granularity }),
    staleTime: 60_000,
  });

  const filteredLines = useMemo(() => {
    const lines = forecastQuery.data?.lines ?? [];
    return lines.filter((line) => {
      if (directionFilter !== "all" && line.direction !== directionFilter) return false;
      if (sourceFilter !== "all" && line.source_kind !== sourceFilter) return false;
      if (locationFilter !== "all") {
        const code = (line.location_code ?? "").trim().toLowerCase();
        if (code !== locationFilter) return false;
      }
      if (search !== "") {
        const haystack = [
          line.document_ref,
          line.counterparty,
          line.detail,
          line.payment_terms_label,
          CASH_FLOW_SOURCE_KIND_LABEL[line.source_kind],
        ]
          .filter((part) => part != null && String(part).trim() !== "")
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    });
  }, [forecastQuery.data?.lines, directionFilter, sourceFilter, locationFilter, search]);

  const bucketRows = useMemo(
    () => flattenBuckets(forecastQuery.data?.buckets ?? [], granularity),
    [forecastQuery.data?.buckets, granularity],
  );

  const sortedLines = useMemo(
    () =>
      [...filteredLines].sort((left, right) => {
        const leftDue = left.due_date ?? "";
        const rightDue = right.due_date ?? "";
        if (leftDue !== rightDue) return leftDue.localeCompare(rightDue);
        return left.document_ref.localeCompare(right.document_ref);
      }),
    [filteredLines],
  );

  const locationCodesInForecast = useMemo(() => {
    const codes = new Set<string>();
    for (const line of forecastQuery.data?.lines ?? []) {
      const code = (line.location_code ?? "").trim().toLowerCase();
      if (code !== "") codes.add(code);
    }
    return [...codes].sort();
  }, [forecastQuery.data?.lines]);
  const unknownExpenses = forecastQuery.data?.unknown_expenses ?? [];
  const totals = forecastQuery.data?.totals;
  const buckets = forecastQuery.data?.buckets ?? [];
  const totalLineCount = forecastQuery.data?.lines.length ?? 0;

  const lineFiltersActive =
    directionFilter !== "all" ||
    sourceFilter !== "all" ||
    locationFilter !== "all" ||
    searchDraft.trim() !== "";

  const pageTabs = useMemo(
    () =>
      [
        { key: "overview" as const, label: "Overview" },
        {
          key: "scheduled" as const,
          label:
            (forecastQuery.data?.lines.length ?? 0) > 0
              ? `Scheduled lines (${forecastQuery.data?.lines.length ?? 0})`
              : "Scheduled lines",
        },
        {
          key: "unknown" as const,
          label:
            unknownExpenses.length > 0
              ? `Unknown expenses (${unknownExpenses.length})`
              : "Unknown expenses",
        },
      ] as const,
    [forecastQuery.data?.lines.length, unknownExpenses.length],
  );

  const apAgingFilter = forecastQuery.data?.ap_aging_filter;
  const arAgingFilter = forecastQuery.data?.ar_aging_filter;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint={HELP_TEXT}>Cash flow forecast</PageTitleWithHelp>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <HoverHintTrigger
            trigger={
              <Button type="button" variant="secondary" onClick={() => setArAgingModalOpen(true)}>
                Import AR aging
              </Button>
            }
          >
            {arAgingImportHoverHint(arAgingFilter)}
          </HoverHintTrigger>
          <HoverHintTrigger
            trigger={
              <Button type="button" variant="secondary" onClick={() => setApAgingModalOpen(true)}>
                Import AP aging
              </Button>
            }
          >
            {apAgingImportHoverHint(apAgingFilter)}
          </HoverHintTrigger>
        </div>
      </div>

      {arAgingModalOpen ? (
        <QboAgingImportModal
          ledger="ar"
          onClose={() => setArAgingModalOpen(false)}
          onImported={() => {
            void queryClient.invalidateQueries({ queryKey: ["accounting", "cash-flow-forecast"] });
          }}
        />
      ) : null}
      {apAgingModalOpen ? (
        <QboAgingImportModal
          ledger="ap"
          onClose={() => setApAgingModalOpen(false)}
          onImported={() => {
            void queryClient.invalidateQueries({ queryKey: ["accounting", "cash-flow-forecast"] });
          }}
        />
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="cash-flow-horizon">Horizon</Label>
              <select
                id="cash-flow-horizon"
                className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={horizonMonths}
                onChange={(event) =>
                  setHorizonMonths(Number.parseInt(event.target.value, 10) as (typeof HORIZON_MONTH_OPTIONS)[number])
                }
              >
                {HORIZON_MONTH_OPTIONS.map((months) => (
                  <option key={months} value={months}>
                    {months} months from {formatDate(from)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="cash-flow-granularity">Granularity</Label>
              <select
                id="cash-flow-granularity"
                className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={granularity}
                onChange={(event) => setGranularity(event.target.value as CashFlowGranularity)}
              >
                <option value="week">Weekly</option>
                <option value="month">Monthly</option>
              </select>
            </div>
          </div>
          {totals ? (
            <p className="mt-3 text-sm text-slate-600">
              Scheduled in horizon (USD): inflow {formatMoney(totals.inflow)}, outflow{" "}
              {formatMoney(totals.outflow)}, net {formatMoney(totals.net)}
              {totals.unknown_outflow > 0 ? (
                <>
                  {" "}
                  ·{" "}
                  <button
                    type="button"
                    className="text-blue-700 underline decoration-blue-700/40 underline-offset-2 hover:decoration-blue-700"
                    onClick={() => setActiveTab("unknown")}
                  >
                    Unknown outflows {formatMoney(totals.unknown_outflow)}
                  </button>
                </>
              ) : null}
            </p>
          ) : null}
        </CardBody>
      </Card>

      {forecastQuery.isError ? (
        <Card>
          <CardBody>
            <p className="text-sm text-rose-700">Could not load cash flow forecast.</p>
          </CardBody>
        </Card>
      ) : null}

      <div
        className="flex flex-wrap gap-2 border-b border-slate-200"
        role="tablist"
        aria-label="Cash flow forecast sections"
      >
        {pageTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            className={cn(
              "-mb-px inline-flex items-center gap-1.5 rounded-t-md border border-transparent px-3 py-2 text-sm font-medium",
              activeTab === tab.key
                ? "border-slate-200 border-b-white bg-white text-slate-900"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
            )}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.key === "unknown" && unknownExpenses.length > 0 ? (
              <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden />
            ) : null}
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "overview" ? (
        forecastQuery.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-slate-600" role="tabpanel">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Loading forecast…
          </div>
        ) : forecastQuery.data ? (
          <div className="space-y-6" role="tabpanel">
            <Card>
              <CardHeader>
                <CardTitle>Cash flow by period (USD)</CardTitle>
              </CardHeader>
              <CardBody>
                <CashFlowBarChart buckets={buckets} granularity={granularity} />
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Timeline summary</CardTitle>
              </CardHeader>
              <CardBody>
                <TimelineSummaryTable rows={bucketRows} />
              </CardBody>
            </Card>
          </div>
        ) : null
      ) : null}

      {activeTab === "scheduled" ? (
        forecastQuery.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-slate-600" role="tabpanel">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Loading scheduled lines…
          </div>
        ) : forecastQuery.data ? (
          <Card role="tabpanel">
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle>Line detail</CardTitle>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={sortedLines.length === 0}
                  onClick={() => downloadCashFlowScheduledLinesCsv(sortedLines, from, to)}
                >
                  <Download className="h-4 w-4" aria-hidden />
                  Download CSV
                </Button>
              </div>
            </CardHeader>
            <CardBody className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
                <p className="mb-3 text-sm font-medium text-slate-800">Filters</p>
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <div>
                    <Label htmlFor="cash-flow-direction">Direction</Label>
                    <select
                      id="cash-flow-direction"
                      className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      value={directionFilter}
                      onChange={(event) =>
                        setDirectionFilter(event.target.value as "all" | CashFlowDirection)
                      }
                    >
                      <option value="all">All</option>
                      <option value="inflow">Inflows only</option>
                      <option value="outflow">Outflows only</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="cash-flow-source">Source</Label>
                    <select
                      id="cash-flow-source"
                      className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      value={sourceFilter}
                      onChange={(event) =>
                        setSourceFilter(event.target.value as "all" | CashFlowSourceKind)
                      }
                    >
                      <option value="all">All sources</option>
                      <option value="open_po">Open purchase order</option>
                      <option value="gipi">GIPI / TSH</option>
                      <option value="cbp_monthly_statement">CBP monthly statement</option>
                      <option value="open_so">Open sales order</option>
                      <option value="gosi">GOSI</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="cash-flow-location">Location</Label>
                    <select
                      id="cash-flow-location"
                      className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      value={locationFilter}
                      onChange={(event) => setLocationFilter(event.target.value)}
                    >
                      <option value="all">All locations</option>
                      {locationCodesInForecast.map((code) => (
                        <option key={code} value={code}>
                          {mrpLocationChipStyles(code).label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="cash-flow-search">Search lines</Label>
                    <Input
                      id="cash-flow-search"
                      type="search"
                      className="mt-1"
                      placeholder="Document, counterparty, terms…"
                      value={searchDraft}
                      onChange={(event) => setSearchDraft(event.target.value)}
                    />
                  </div>
                </div>
                <p className="mt-3 text-sm text-slate-600">
                  {lineFiltersActive
                    ? `Showing ${sortedLines.length} of ${totalLineCount} scheduled lines`
                    : `${totalLineCount} scheduled lines`}
                </p>
              </div>
              <LineTable
                rows={sortedLines}
                emptyMessage="No lines match the current filters in this horizon."
              />
            </CardBody>
          </Card>
        ) : null
      ) : null}

      {activeTab === "unknown" ? (
        <Card role="tabpanel">
          <CardHeader>
            <CardTitle>Unknown expenses</CardTitle>
          </CardHeader>
          <CardBody>
            {forecastQuery.isLoading ? (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Loading…
              </div>
            ) : forecastQuery.isError ? null : (
              <UnknownExpensesPanel rows={unknownExpenses} />
            )}
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}
